/**
 * Support Store - AImry Chat State Management
 *
 * Zustand store for managing chat widget state and messages.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ChatMessage, DeviceInfo } from '../types';

interface SupportState {
  // Widget state
  isOpen: boolean;
  isMinimized: boolean;

  // Current conversation
  conversationId: string | null;
  messages: ChatMessage[];
  isLoading: boolean;

  // Input state
  inputValue: string;

  // Voice state
  isListening: boolean;
  isSpeaking: boolean;

  // Device info (captured once)
  deviceInfo: DeviceInfo | null;

  // Actions
  setIsOpen: (isOpen: boolean) => void;
  toggleOpen: () => void;
  setIsMinimized: (isMinimized: boolean) => void;
  setConversationId: (id: string | null) => void;
  addMessage: (message: ChatMessage) => void;
  setMessages: (messages: ChatMessage[]) => void;
  clearMessages: () => void;
  setIsLoading: (isLoading: boolean) => void;
  setInputValue: (value: string) => void;
  setIsListening: (isListening: boolean) => void;
  setIsSpeaking: (isSpeaking: boolean) => void;
  setDeviceInfo: (info: DeviceInfo) => void;
  reset: () => void;
}

const initialState = {
  isOpen: false,
  isMinimized: false,
  conversationId: null,
  messages: [],
  isLoading: false,
  inputValue: '',
  isListening: false,
  isSpeaking: false,
  deviceInfo: null,
};

export const useSupportStore = create<SupportState>()(
  persist(
    (set) => ({
      ...initialState,

      setIsOpen: (isOpen) => set({ isOpen, isMinimized: false }),

      toggleOpen: () => set((state) => ({
        isOpen: !state.isOpen,
        isMinimized: false
      })),

      setIsMinimized: (isMinimized) => set({ isMinimized }),

      setConversationId: (conversationId) => set({ conversationId }),

      addMessage: (message) => set((state) => ({
        messages: [...state.messages, message]
      })),

      setMessages: (messages) => set({ messages }),

      clearMessages: () => set({ messages: [], conversationId: null }),

      setIsLoading: (isLoading) => set({ isLoading }),

      setInputValue: (inputValue) => set({ inputValue }),

      setIsListening: (isListening) => set({ isListening }),

      setIsSpeaking: (isSpeaking) => set({ isSpeaking }),

      setDeviceInfo: (deviceInfo) => set({ deviceInfo }),

      reset: () => set(initialState),
    }),
    {
      name: 'aimry-support-storage',
      partialize: (state) => ({
        // Only persist these fields
        conversationId: state.conversationId,
        messages: state.messages,
      }),
    }
  )
);

/**
 * Hook to capture device info
 */
export const captureDeviceInfo = (): DeviceInfo => {
  const userAgent = navigator.userAgent;

  // Parse browser name and version
  let browserName = 'Unknown';
  let browserVersion = '';

  if (userAgent.includes('Firefox/')) {
    browserName = 'Firefox';
    browserVersion = userAgent.match(/Firefox\/(\d+(\.\d+)?)/)?.[1] || '';
  } else if (userAgent.includes('Chrome/') && !userAgent.includes('Edg/')) {
    browserName = 'Chrome';
    browserVersion = userAgent.match(/Chrome\/(\d+(\.\d+)?)/)?.[1] || '';
  } else if (userAgent.includes('Safari/') && !userAgent.includes('Chrome/')) {
    browserName = 'Safari';
    browserVersion = userAgent.match(/Version\/(\d+(\.\d+)?)/)?.[1] || '';
  } else if (userAgent.includes('Edg/')) {
    browserName = 'Edge';
    browserVersion = userAgent.match(/Edg\/(\d+(\.\d+)?)/)?.[1] || '';
  }

  // Parse OS name
  let osName = 'Unknown';
  if (userAgent.includes('Windows')) {
    osName = 'Windows';
  } else if (userAgent.includes('Mac OS')) {
    osName = 'macOS';
  } else if (userAgent.includes('Linux')) {
    osName = 'Linux';
  } else if (userAgent.includes('Android')) {
    osName = 'Android';
  } else if (userAgent.includes('iOS') || userAgent.includes('iPhone') || userAgent.includes('iPad')) {
    osName = 'iOS';
  }

  // Screen size
  const screenSize = `${window.innerWidth}x${window.innerHeight}`;

  return {
    userAgent,
    browserName,
    browserVersion,
    osName,
    screenSize,
  };
};
